import { CompleteMultipartVideoUploadRequest, UploadPart } from '@eigtech/meetings-types'
import { Corn } from '@eigtech/shared-corn'
import {
  abortMultipartVideoUpload,
  completeMultipartVideoUpload,
  getMultipartVideoUploadUrls,
  initializeMultipartVideoUpload,
} from '../api/multipartVideoUpload'
import { getMediaMimeType } from './getMediaMimeType'

type MeetingInfo = {
  description: string
  entityId: Corn
  metadata: Record<string, string>
  notes: string
  requiresStitching: boolean
  startTime: string
}

type UploaderOptions = {
  chunkSize?: number
  file: File
  meetingInfo: MeetingInfo
  threadsQuantity?: number
}

type Uploaded = CompleteMultipartVideoUploadRequest['uploadedParts'][number]

type Progress = {
  sent: number
  total: number
  percentage: number
}

// #tuning
const DEFAULT_CHUNK_SIZE = 1024 * 1024 * 5 // 5MB
const DEFAULT_THREAD_COUNT = 5
const MAX_THREAD_COUNT = 15
const RETRY_COUNT = 6

// original source: https://github.com/aws-samples/amazon-s3-multipart-upload-transfer-acceleration/blob/main/frontendV2/src/utils/upload.js
export class MultipartUploader {
  private aborted: boolean
  private activeConnections: Record<string, XMLHttpRequest>
  /** this must be bigger than or equal to 5MB, otherwise AWS will respond with:
   * "Your proposed upload is smaller than the minimum allowed size" */
  private chunkSize: number
  private contentType: string
  private file: File
  private meetingInfo: MeetingInfo
  private parts: UploadPart[]
  private progressCache: Record<number, number>
  // number of parallel uploads
  private threadsQuantity: number
  // adjust the timeout value to activate exponential backoff retry strategy
  private timeout: number
  private uploadedParts: Uploaded[]
  private uploadedSize: number
  private uploadId: string
  private onCompleteFn: () => void
  private onErrorFn: (error: Error) => void
  private onProgressFn: (progress: Progress) => void

  constructor(options: UploaderOptions) {
    const chunkSize = options.chunkSize || 0
    this.chunkSize = Math.max(1024 * 1024 * chunkSize, DEFAULT_CHUNK_SIZE)

    const threadsQuantity = options.threadsQuantity || 0
    this.threadsQuantity = Math.min(threadsQuantity || DEFAULT_THREAD_COUNT, MAX_THREAD_COUNT)

    this.file = options.file
    this.contentType = getMediaMimeType(this.file) ?? ''
    this.meetingInfo = options.meetingInfo
    this.timeout = 0

    this.aborted = false
    this.activeConnections = {}
    this.uploadId = ''
    this.onCompleteFn = () => {}
    this.onErrorFn = () => {}
    this.onProgressFn = () => {}
    this.parts = []
    this.progressCache = {}
    this.uploadedParts = []
    this.uploadedSize = 0
  }

  start() {
    this.initialize()
  }

  private async initialize() {
    try {
      const initializeResponse = await initializeMultipartVideoUpload({
        ...this.meetingInfo,
        filename: this.file.name,
        contentType: this.contentType,
      })

      this.uploadId = initializeResponse.uploadId

      const numberOfParts = Math.ceil(this.file.size / this.chunkSize)

      const urlsResponse = await getMultipartVideoUploadUrls({
        ...this.meetingInfo,
        filename: this.file.name,
        parts: numberOfParts,
        uploadId: this.uploadId,
      })

      const newParts = urlsResponse.multipartVideoUploadUrls
      this.parts.push(...newParts)

      this.sendNext()
    } catch (error) {
      await this.complete(error)
    }
  }

  private sendNext(retry = 0) {
    const activeConnections = Object.keys(this.activeConnections).length

    if (activeConnections >= this.threadsQuantity) {
      return
    }

    if (!this.parts.length) {
      if (!activeConnections) {
        this.complete()
      }

      return
    }

    const part = this.parts.pop()
    if (this.file && part) {
      const sentSize = (part.partNumber - 1) * this.chunkSize
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize)

      const sendChunkStarted = () => {
        this.sendNext()
      }

      this.sendChunk(chunk, part, sendChunkStarted)
        .then(() => {
          this.sendNext()
        })
        .catch((error) => {
          if (retry <= RETRY_COUNT && !this.aborted) {
            retry++
            const wait = (ms: number) => new Promise((res) => setTimeout(res, ms))
            //exponential backoff retry before giving up
            // eslint-disable-next-line no-console
            console.warn(
              `Part#${part.partNumber} failed to upload, backing off ${2 ** retry * 100} before retrying...`
            )
            wait(2 ** retry * 100).then(() => {
              this.parts.push(part)
              this.sendNext(retry)
            })
          } else {
            // eslint-disable-next-line no-console
            console.warn(`Part#${part.partNumber} failed to upload, giving up`)
            this.complete(error)
          }
        })
    }
  }

  private async complete(error?: unknown) {
    if (error) {
      this.onErrorFn(error as Error)
      return
    } else if (this.aborted) {
      this.onErrorFn(new Error('cannot complete - aborted'))
      return
    }

    try {
      await this.sendCompleteRequest()
      this.onCompleteFn()
    } catch (error) {
      this.onErrorFn(error as Error)
    }
  }

  private async sendCompleteRequest() {
    if (this.uploadId) {
      await completeMultipartVideoUpload({
        ...this.meetingInfo,
        filename: this.file.name,
        uploadId: this.uploadId,
        uploadedParts: this.uploadedParts,
      })
    }
  }

  private sendChunk(chunk: Blob, part: UploadPart, sendChunkStarted: () => void) {
    return new Promise<void>((resolve, reject) => {
      this.upload(chunk, part, sendChunkStarted)
        .then((status) => {
          if (status !== 200) {
            reject(new Error('Failed chunk upload'))
            return
          }

          resolve()
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  private handleProgress(part: number, event: ProgressEvent) {
    if (this.file) {
      if (event.type === 'progress' || event.type === 'error' || event.type === 'abort') {
        this.progressCache[part] = event.loaded
      }

      if (event.type === 'uploaded') {
        this.uploadedSize += this.progressCache[part] || 0
        delete this.progressCache[part]
      }

      const inProgress = Object.keys(this.progressCache)
        .map(Number)
        .reduce((memo, id) => (memo += this.progressCache[id]), 0)

      const sent = Math.min(this.uploadedSize + inProgress, this.file.size)

      const total = this.file.size

      const percentage = Math.round((sent / total) * 100)

      this.onProgressFn({ sent, total, percentage })
    }
  }

  private upload(file: Blob, part: UploadPart, sendChunkStarted: () => void) {
    // uploading each part with its pre-signed URL
    return new Promise((resolve, reject) => {
      const throwXHRError = (error: Error, part: UploadPart, abortFx: EventListener) => {
        delete this.activeConnections?.[part.partNumber - 1]
        reject(error)
        window.removeEventListener('offline', abortFx)
      }
      if (this.uploadId) {
        // Gracefully handle offline scenarios
        if (!window.navigator.onLine) reject(new Error('System is offline'))

        const xhr = (this.activeConnections[part.partNumber - 1] = new XMLHttpRequest())
        xhr.timeout = this.timeout
        sendChunkStarted()

        const progressListener = this.handleProgress.bind(this, part.partNumber - 1)

        xhr.upload.addEventListener('progress', progressListener)

        xhr.addEventListener('error', progressListener)
        xhr.addEventListener('abort', progressListener)
        xhr.addEventListener('loadend', progressListener)

        xhr.open('PUT', part.signedUrl)
        xhr.setRequestHeader('Content-Type', this.contentType)
        const abortXHR = () => xhr.abort()
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            const eTag = xhr.getResponseHeader('ETag')

            if (eTag) {
              const uploadedPart = {
                partNumber: part.partNumber,
                eTag: eTag.replaceAll('"', ''),
              }

              this.uploadedParts.push(uploadedPart)

              resolve(xhr.status)
              delete this.activeConnections[part.partNumber - 1]
              window.removeEventListener('offline', abortXHR)
            }
          } else if (xhr.status && `${xhr.status}`.charAt(0) !== '2') {
            this.complete(new Error(`${xhr.status}: ${xhr.statusText}`))
          }
        }

        xhr.onerror = (error: unknown) => {
          throwXHRError(error as Error, part, abortXHR)
        }
        xhr.ontimeout = (error: unknown) => {
          throwXHRError(error as Error, part, abortXHR)
        }
        xhr.onabort = () => {
          throwXHRError(new Error('Upload canceled by user or system'), part, abortXHR)
        }
        window.addEventListener('offline', abortXHR)
        xhr.send(file)
      }
    })
  }

  // #region Public
  onProgress(onProgress: (progress: Progress) => void) {
    this.onProgressFn = onProgress
    return this
  }

  onComplete(onComplete: () => void) {
    this.onCompleteFn = onComplete
    return this
  }

  onError(onError: (error: Error) => void) {
    this.onErrorFn = onError
    return this
  }

  // #region Helpers
  async abort() {
    Object.keys(this.activeConnections)
      .map(Number)
      .forEach((id) => {
        this.activeConnections[id].abort()
      })

    this.aborted = true
    try {
      await abortMultipartVideoUpload({
        ...this.meetingInfo,
        filename: this.file.name,
        uploadId: this.uploadId,
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('unable to abort upload', (err as Error).message)
    }
  }
}
